import { createApp } from "vue";
import App from "./App.vue";
import router from "./router";
import store from "./store";
import "./assets/global.less";
import { http, httpAuthorized } from "@/util/http";
import GlobalComponents from "@/plugin/GlobalComponents";
import ErrorHandler from "./util/ErrorHandler";
import "element-plus/dist/index.css";
import * as ElementPlusIconsVue from "@element-plus/icons-vue";
import i18n from "./assets/i18n";
import "@/assets/icons.css";
import ElementPlus from "element-plus";

/**
 * 全局 ResizeObserver 防抖补丁
 *
 * 背景：Element Plus 的 el-select / el-tooltip / popper 等组件内部使用 ResizeObserver，
 * 当下拉选择引发列表重渲染、容器尺寸连锁变化时，会在同一帧内再次触发回调，
 * Chrome 会抛出 "ResizeObserver loop completed with undelivered notifications" 良性警告，
 * 并被 webpack-dev-server 的 overlay 捕获为全屏红色运行时错误（干扰开发调试）。
 *
 * 方案：对 ResizeObserver 回调做 16ms（约一帧）防抖，打破"回调→布局→回调"循环，
 * 从源头消除该报错。对组件功能无感知（仅延迟一帧执行）。
 */
const OriginalResizeObserver = window.ResizeObserver;
if (OriginalResizeObserver) {
  window.ResizeObserver = class DebouncedResizeObserver extends (
    OriginalResizeObserver
  ) {
    constructor(callback: ResizeObserverCallback) {
      let timer: number | null = null;
      const debounced: ResizeObserverCallback = (entries, observer) => {
        if (timer !== null) {
          window.clearTimeout(timer);
        }
        timer = window.setTimeout(() => {
          timer = null;
          callback(entries, observer);
        }, 16);
      };
      super(debounced);
    }
  };
}

const app = createApp(App);

for (const [key, component] of Object.entries(ElementPlusIconsVue)) {
  app.component(key, component);
}

router.beforeEach((to, from, next) => {
  const token = localStorage.getItem("token");

  if (to.matched.some((record) => record.meta.requiresAuth)) {
    if (!token) {
      next({ name: "login" });
      return;
    }
  }

  if (to.matched.length === 0) {
    from.name ? next({ name: from.name }) : next("/");
    return;
  }

  next();
});

app.config.errorHandler = (err, instance, info) => {
  console.log(instance, info);
  ErrorHandler.handleError(err);
};

/**
 * 静默第三方库内部 ResizeObserver polyfill 的销毁竞态报错
 *
 * 现象：路由切换（组件销毁）后 console 抛
 *   "Uncaught TypeError: Cannot read properties of undefined (reading 'startTime')"
 *   at et.reportAllChanges ... at n.timeout ... requestIdleCallback
 *
 * 根因：第三方库（地图/图表类）内部 ResizeObserver polyfill 在组件销毁后，
 * 已排队的 requestIdleCallback 回调仍执行并访问已销毁对象。属库内部 bug，
 * 发生在页面切走之后，无功能影响，无法从业务代码根治。
 *
 * 处理：window.onerror 精确匹配该特征时返回 true 阻止默认打印；其余错误不受影响。
 */
window.onerror = (msg, _src, _line, _col, error) => {
  if (
    error instanceof Error &&
    /reportAllChanges/.test(error.stack ?? "") &&
    /reading 'startTime'/.test(error.message)
  ) {
    return true; // 已处理：阻止默认 console 打印
  }
  return false;
};
app.use(i18n);

// 语言
app.use(ElementPlus);
app.use(GlobalComponents).use(store).use(router).mount("#app");

export { http, httpAuthorized };
